import React, { useEffect, useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, Pencil, Trash2, Clock, Bell, BellOff, AlertTriangle, TrendingUp, Mail, Loader2, GitBranch, Play, History, Slack, Users, Sparkles } from 'lucide-react';
import { VersionHistoryButton } from '@/components/VersionHistory';
import { useQueryParamHighlight } from '@/hooks/useQueryParamHighlight';
import { ConditionBuilder, conditionSummary } from '@/components/ConditionBuilder';
import SchedulePicker, { defaultScheduleSpec } from '@/components/SchedulePicker';
import { toast } from 'sonner';
import { useCan } from '@/lib/permissions';
import { SlackChannelPicker } from '@/components/ChannelPickers';

const SEV_COLORS = { critical: 'bg-red-500/10 border-red-500/30 text-red-400', high: 'bg-orange-500/10 border-orange-500/30 text-orange-400', medium: 'bg-amber-500/10 border-amber-500/30 text-amber-400', low: 'bg-sky-500/10 border-sky-500/30 text-sky-400' };

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// Human label for the unified schedule spec (mode-based). Mirrors backend describe_spec.
function describeUnified(s) {
  const tod = s.time ? `${String(s.time.hour ?? 8).padStart(2, '0')}:${String(s.time.minute ?? 0).padStart(2, '0')}` : '';
  if (s.mode === 'hourly') { const h = s.hourly?.hours || 0, m = s.hourly?.minutes || 0; return `Every ${h ? h + 'h ' : ''}${m ? m + 'm' : ''}`.trim() || 'Hourly'; }
  if (s.mode === 'daily') { const d = (s.daily?.weekdays || []); return d.length ? `${d.map(i => DAYS[i]).join(', ')} at ${tod}` : `Daily at ${tod}`; }
  if (s.mode === 'weekly') { const d = (s.weekly?.weekdays || []).map(i => DAYS[i]).join(', '); const n = s.weekly?.interval || 1; return `Every ${n > 1 ? n + ' wks' : 'week'} ${d} at ${tod}`.replace(/\s+/g, ' ').trim(); }
  if (s.mode === 'monthly') { const wk = { 1: '1st', 2: '2nd', 3: '3rd', 4: '4th', 5: '5th', '-1': 'Last' }[s.monthly?.week] || '1st'; const d = (s.monthly?.weekdays || []).map(i => DAYS[i]).join(', '); return `${wk} ${d} at ${tod}`.replace(/\s+/g, ' ').trim(); }
  return 'Schedule';
}
function formatSchedule(s) {
  if (!s) return 'Not set';
  if (s.mode) return describeUnified(s);
  const time = s.time || '09:00';
  switch (s.type) {
    case 'hourly': return `Every ${s.interval || 1} hour${(s.interval || 1) > 1 ? 's' : ''}`;
    case 'daily': return `Daily at ${time}`;
    case 'weekly': {
      const days = (s.days_of_week || []).map(i => DAYS[i]).join(', ');
      return days ? `${days} at ${time}` : `Weekly at ${time}`;
    }
    case 'monthly': return `${s.day_of_month || 1}${nth(s.day_of_month || 1)} of each month at ${time}`;
    case 'monthly_ordinal': return `${cap(s.ordinal || 'first')} ${cap(s.ordinal_day || 'monday')} at ${time}`;
    case 'quarterly': return `Quarterly at ${time}`;
    default: return s.type || 'Not set';
  }
}
function nth(n) { const s = ['th','st','nd','rd']; const v = n % 100; return s[(v-20)%10] || s[v] || s[0]; }
function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; }

export default function AlertsPage() {
  const canWrite = useCan('alerts:write');
  const [searchParams, setSearchParams] = useSearchParams();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [editAlert, setEditAlert] = useState(null);
  const [alertName, setAlertName] = useState('');
  const [alertMetric, setAlertMetric] = useState('');
  const [alertOp, setAlertOp] = useState('gt');
  const [alertThreshold, setAlertThreshold] = useState('');
  const [alertSchedule, setAlertSchedule] = useState(defaultScheduleSpec());
  const [alertRecipients, setAlertRecipients] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('medium');
  const [alertSourceType, setAlertSourceType] = useState('model');
  const [alertModelId, setAlertModelId] = useState('');
  const [alertCollection, setAlertCollection] = useState('');
  const [alertFields, setAlertFields] = useState([]);
  const [alertConditions, setAlertConditions] = useState({ logic: 'AND', rules: [] });
  const [alertDimensionFilters, setAlertDimensionFilters] = useState([]);
  const [alertDatePreset, setAlertDatePreset] = useState('');
  const [alertChannels, setAlertChannels] = useState(['email']);
  const [alertSlackChannel, setAlertSlackChannel] = useState('');
  const [alertPredictive, setAlertPredictive] = useState(false);
  const [alertForecastHorizon, setAlertForecastHorizon] = useState(6);
  const [alertForecastUnit, setAlertForecastUnit] = useState('month');
  const [alertAiText, setAlertAiText] = useState('');
  const [aiDrafting, setAiDrafting] = useState(false);
  const [aiDraftPreview, setAiDraftPreview] = useState(null);
  const [confirmDeleteAlert, setConfirmDeleteAlert] = useState(null);

  const OP_LABELS = { gt: '>', lt: '<', gte: '≥', lte: '≤', eq: '=', neq: '≠' };
  const draftSummary = (d) => {
    const met = metricLabel(d.metric) || d.metric || 'metric';
    const base = `${met} ${OP_LABELS[d.operator] || d.operator} ${d.threshold ?? ''}`;
    const pred = d.predictive ? ` · predictive over ${d.forecast_horizon} ${d.forecast_unit}(s)` : '';
    return `${base}${pred} · ${d.severity} severity`;
  };

  const draftWithAi = async () => {
    if (!alertAiText.trim()) return;
    setAiDrafting(true);
    try {
      const { draft } = await api.aiDraftAlert({ text: alertAiText, model_id: alertModelId || undefined });
      setAiDraftPreview(draft);
    } catch (e) { toast.error(e.message || 'AI draft failed'); }
    finally { setAiDrafting(false); }
  };

  const applyDraft = () => {
    const draft = aiDraftPreview;
    if (!draft) return;
    if (draft.name) setAlertName(draft.name);
    if (draft.metric) setAlertMetric(draft.metric);
    if (draft.operator) setAlertOp(draft.operator);
    if (draft.threshold !== null && draft.threshold !== undefined) setAlertThreshold(String(draft.threshold));
    if (draft.severity) setAlertSeverity(draft.severity);
    setAlertPredictive(!!draft.predictive);
    if (draft.forecast_horizon) setAlertForecastHorizon(draft.forecast_horizon);
    if (draft.forecast_unit) setAlertForecastUnit(draft.forecast_unit);
    setUseAdvanced(false);
    setAiDraftPreview(null);
    toast.success('Draft applied — review and save');
  };
  const [historyAlert, setHistoryAlert] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [useAdvanced, setUseAdvanced] = useState(false);
  const [smModels, setSmModels] = useState([]);
  const [alertDataSources, setAlertDataSources] = useState([]);
  const [metricLabels, setMetricLabels] = useState({});

  // Viewer-facing metric name: prefer the semantic display/business label,
  // fall back to a humanised (Title Case) physical name — never the raw column.
  const metricLabel = (name) => metricLabels[name] || String(name || '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());

  useEffect(() => {
    Promise.all([api.getAlerts(), api.getSemanticModels().catch(() => []), api.getDataSources().catch(() => [])])
      .then(([a, sm, ds]) => {
        setAlerts(a); setSmModels(sm || []); setAlertDataSources(ds || []);
        // Build a metric→display-name map so list badges show business labels
        // instead of physical columns. Some alerts (created from AI Explore)
        // carry no model_id, so scan every accessible model's fields, not just
        // the ones referenced by an alert.model_id.
        const alertModelIds = (a || []).map(al => al.model_id).filter(Boolean);
        const allModelIds = (sm || []).map(m => m.model_id).filter(Boolean);
        const modelIds = [...new Set([...alertModelIds, ...allModelIds])];
        Promise.all(modelIds.map(id => api.getSemanticModelFull(id).catch(() => null)))
          .then(models => {
            const map = {};
            (models || []).forEach(m => {
              (m?.entities || []).forEach(e => (e.attributes || []).forEach(attr => {
                const lbl = attr.business_name || attr.display_name || attr.label;
                if (lbl && !map[attr.name]) map[attr.name] = lbl;
              }));
              (m?.calculated_measures || []).forEach(cm => {
                const lbl = cm.business_name || cm.display_name || cm.label;
                if (lbl && !map[cm.name]) map[cm.name] = lbl;
              });
            });
            if (Object.keys(map).length) setMetricLabels(map);
          });
      })
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  useQueryParamHighlight('alert', alerts, 'alert');

  // When arriving from AI Explore's "Create Alert" (→ /alerts?edit=<id>),
  // open the Edit Alert dialog pre-filled so the user can customize it
  // without a second navigation.
  const [editParamHandled, setEditParamHandled] = useState(false);
  useEffect(() => {
    const editId = searchParams.get('edit');
    const create = searchParams.get('create');
    if (loading || editParamHandled) return;
    if (editId) {
      const target = alerts.find(a => a.alert_id === editId);
      if (target) {
        openEdit(target);
        setEditParamHandled(true);
        const next = new URLSearchParams(searchParams); next.delete('edit'); setSearchParams(next, { replace: true });
      }
      return;
    }
    if (create === 'true') {
      // Prefilled create flow (e.g. "Create Alert" from a visual). Preselect the
      // model so the data-source dropdown isn't blank, and seed metric/threshold.
      openCreate();
      const metric = searchParams.get('metric') || '';
      const name = searchParams.get('name') || '';
      const threshold = searchParams.get('threshold') || '';
      const modelId = searchParams.get('model_id') || '';
      const dim = searchParams.get('dim') || '';
      const dimVal = searchParams.get('dimval') || '';
      if (name) setAlertName(name);
      if (metric) setAlertMetric(metric);
      if (threshold) setAlertThreshold(threshold);
      if (modelId) {
        setAlertSourceType('model');
        setAlertModelId(modelId);
        api.getSemanticModelFull(modelId).then(m => {
          const f = []; (m.entities || []).forEach(e => (e.attributes || []).forEach(attr => { if (!f.some(ff => ff.name === attr.name)) f.push(attr); })); setAlertFields(f);
          // Ensure the model appears in the source dropdown so its name shows
          // (not the "Select model" placeholder) even if the list fetch lagged.
          setSmModels(prev => prev.some(x => x.model_id === modelId) ? prev : [...prev, { model_id: modelId, name: m.name || modelId }]);
        }).catch(() => {});
      }
      // Auto-scope by the clicked data point's dimension (e.g. Line of Business = Personal Auto).
      if (dim && dimVal) {
        setAlertName(name || `Alert: ${metric} for ${dimVal}`);
        setAlertDimensionFilters([{ field: dim, operator: 'equals', value: dimVal }]);
      } else {
        setAlertDimensionFilters([]);
      }
      setEditParamHandled(true);
      const next = new URLSearchParams(searchParams);
      ['create', 'metric', 'name', 'threshold', 'model_id', 'dim', 'dimval'].forEach(k => next.delete(k));
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, alerts, loading, editParamHandled]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => { setEditAlert(null); setAlertName(''); setAlertMetric(''); setAlertOp('gt'); setAlertThreshold(''); setAlertSchedule(defaultScheduleSpec()); setAlertRecipients(''); setAlertSeverity('medium'); setAlertSourceType('model'); setAlertModelId(''); setAlertCollection(''); setAlertFields([]); setAlertConditions({ logic: 'AND', rules: [{ metric: '', operator: 'gt', threshold: '' }] }); setAlertDimensionFilters([]); setAlertDatePreset(''); setAlertChannels(['email']); setAlertSlackChannel(''); setUseAdvanced(false); setAlertPredictive(false); setAlertForecastHorizon(6); setAlertForecastUnit('month'); setAlertAiText(''); setAiDraftPreview(null); setAlertDialogOpen(true); };
  const location = useLocation();
  useEffect(() => {
    const p = location.state?.prefillAlert;
    if (!p) return;
    openCreate();
    if (p.name) setAlertName(p.name);
    if (p.metric) setAlertMetric(p.metric);
    if (p.model_id) { setAlertSourceType('model'); setAlertModelId(p.model_id); }
    if (p.operator) setAlertOp(p.operator);
    if (p.threshold !== undefined && p.threshold !== '') setAlertThreshold(String(p.threshold));
    if (p.severity) setAlertSeverity(p.severity);
    setAlertPredictive(!!p.predictive);
    if (p.forecast_horizon) setAlertForecastHorizon(p.forecast_horizon);
    if (p.forecast_unit) setAlertForecastUnit(p.forecast_unit);
    window.history.replaceState({}, document.title);
     
  }, [location.state]);

  const openEdit = (a) => {
    setEditAlert(a); setAlertName(a.name); setAlertMetric(a.metric); setAlertOp(a.operator); setAlertThreshold(String(a.threshold)); setAlertSchedule(a.schedule?.mode ? a.schedule : defaultScheduleSpec()); setAlertRecipients((a.recipients || []).join(', ')); setAlertSeverity(a.severity); setAlertSourceType(a.source_type || 'model'); setAlertModelId(a.model_id || ''); setAlertCollection(a.collection || '');
    setAlertSourceType(a.source_type || 'model'); setAlertModelId(a.model_id || ''); setAlertCollection(a.collection || '');
    setAlertDimensionFilters(a.dimension_filters || []);
    setAlertDatePreset(a.date_preset || '');
    setAlertChannels(a.channels && a.channels.length ? a.channels : (a.recipients?.length ? ['email'] : []));
    setAlertSlackChannel(a.slack_channel || '');
    setAlertPredictive(!!a.predictive); setAlertForecastHorizon(a.forecast_horizon || 6); setAlertForecastUnit(a.forecast_unit || 'month'); setAlertAiText(''); setAiDraftPreview(null);
    // Load advanced conditions if present
    if (a.conditions?.rules?.length > 0) {
      setAlertConditions(a.conditions);
      setUseAdvanced(true);
    } else {
      setAlertConditions({ logic: 'AND', rules: [{ metric: a.metric || '', operator: a.operator || 'gt', threshold: a.threshold ?? '' }] });
      setUseAdvanced(false);
    }
    setAlertDialogOpen(true);
    // Load fields for the model
    if (a.model_id) api.getSemanticModelFull(a.model_id).then(m => { const f = []; (m.entities || []).forEach(e => (e.attributes || []).forEach(attr => { if (!f.some(ff => ff.name === attr.name)) f.push(attr); })); setAlertFields(f); setSmModels(prev => prev.some(x => x.model_id === a.model_id) ? prev : [...prev, { model_id: a.model_id, name: m.name || a.model_id }]); }).catch(() => {});
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        name: alertName,
        metric: useAdvanced ? (alertConditions.rules[0]?.metric || alertMetric) : alertMetric,
        operator: useAdvanced ? (alertConditions.rules[0]?.operator || alertOp) : alertOp,
        threshold: useAdvanced ? Number(alertConditions.rules[0]?.threshold || alertThreshold) : Number(alertThreshold),
        check_frequency: alertSchedule.mode,
        schedule: alertSchedule,
        recipients: alertRecipients.split(',').map(s => s.trim()).filter(Boolean),
        severity: alertSeverity,
        source_type: alertSourceType,
        model_id: alertModelId || undefined,
        collection: alertCollection || undefined,
        conditions: useAdvanced ? alertConditions : undefined,
        dimension_filters: alertDimensionFilters.length ? alertDimensionFilters : undefined,
        date_preset: alertDatePreset || undefined,
        channels: alertChannels,
        slack_channel: alertSlackChannel || undefined,
        predictive: alertPredictive,
        forecast_horizon: alertPredictive ? Number(alertForecastHorizon) || 6 : undefined,
        forecast_unit: alertPredictive ? alertForecastUnit : undefined,
      };
      if (editAlert) { const u = await api.updateAlert(editAlert.alert_id, data); setAlerts(prev => prev.map(a => a.alert_id === editAlert.alert_id ? u : a)); }
      else { const n = await api.createAlert(data); setAlerts(prev => [...prev, n]); }
      setAlertDialogOpen(false); toast.success(editAlert ? 'Alert updated' : 'Alert created');
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => { try { await api.deleteAlert(id); setAlerts(prev => prev.filter(a => a.alert_id !== id)); toast.success('Alert deleted'); } catch (err) { toast.error(err.message); } };
  const handleToggle = async (id) => { try { const a = alerts.find(x => x.alert_id === id); await api.updateAlert(id, { enabled: !a.enabled }); setAlerts(prev => prev.map(x => x.alert_id === id ? { ...x, enabled: !x.enabled } : x)); } catch (err) { toast.error(err.message); } };
  const handleEvaluate = async (id) => {
    try {
      const r = await api.evaluateAlert(id, true);
      const cv = typeof r.current_value === 'number' ? r.current_value.toLocaleString() : (r.current_value ?? '—');
      const rng = r.resolved_range ? ` (window ${r.resolved_range.gte || '…'} → ${r.resolved_range.lt || '…'})` : '';
      const pinged = r.notified ? ' — notification sent' : '';
      if (r.predictive && r.forecast) {
        const f = r.forecast;
        toast[r.breached ? 'warning' : 'success'](`${r.breached ? 'Projected breach' : 'No projected breach'}: ${cv} by ${f.projected_period} vs ${r.operator} ${r.threshold} · ${f.confidence} confidence${pinged}`);
        setAlerts(prev => prev.map(a => a.alert_id === id ? { ...a, last_forecast: f } : a));
      } else {
        toast[r.breached ? 'warning' : 'success'](`${r.breached ? 'Breached' : 'OK'}: current ${cv} vs ${r.operator} ${r.threshold}${rng}${pinged}`);
      }
      if (historyAlert && historyAlert.alert_id === id) openHistory(historyAlert);
    } catch (err) { toast.error(err.message); }
  };
  const openHistory = async (alert) => {
    setHistoryAlert(alert); setHistoryLoading(true); setHistoryRows([]);
    try {
      const r = await api.getAlertHistory(alert.alert_id);
      setHistoryRows(r.evaluations || []);
    } catch (err) { toast.error(err.message); } finally { setHistoryLoading(false); }
  };
  const handleSnooze = async (id, minutes) => {
    try {
      const r = await api.snoozeAlert(id, minutes);
      setAlerts(prev => prev.map(x => x.alert_id === id ? { ...x, snoozed_until: r.snoozed_until } : x));
      toast.success(minutes ? `Snoozed until ${new Date(r.snoozed_until).toLocaleString()}` : 'Alert un-snoozed');
    } catch (err) { toast.error(err.message); }
  };

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>;

  return (
    <TooltipProvider>
    <div className="p-6 space-y-6" data-testid="alerts-page">
      <div className="flex items-center justify-between animate-slide-up">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground">Alerts</h1>
          <p className="text-sm text-muted-foreground mt-1">Monitor key metrics and get notified</p>
        </div>
        {canWrite && <Button onClick={openCreate} className="rounded-xl bg-primary text-primary-foreground active:scale-95 text-xs" data-testid="create-alert-btn" data-coach-target="alerts-new-button"><Plus className="w-3.5 h-3.5 mr-1.5" /> New Alert</Button>}
      </div>

      <div className="space-y-3" data-testid="alerts-list">
        {alerts.length === 0 ? (
          <div className="flex flex-col items-center py-20 text-center">
            <div className="p-4 rounded-2xl bg-amber-500/5 border border-amber-500/10 mb-4"><AlertTriangle className="w-8 h-8 text-amber-400/40" /></div>
            <h3 className="text-base font-heading font-semibold text-foreground mb-1">No alerts configured</h3>
            <p className="text-sm text-muted-foreground max-w-sm mb-4">Set up alerts to monitor metrics and get notified when thresholds are exceeded.</p>
            {canWrite && <Button onClick={openCreate} className="rounded-xl" data-coach-target="alerts-new-button"><Plus className="w-4 h-4 mr-2" /> Create Alert</Button>}
          </div>
        ) : alerts.map(alert => (
          <div key={alert.alert_id} className="glass-card rounded-2xl p-5 group" data-testid={`alert-${alert.alert_id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-xl border ${SEV_COLORS[alert.severity] || SEV_COLORS.medium}`}><AlertTriangle className="w-5 h-5" /></div>
                <div>
                  <h3 className="text-sm font-heading font-semibold text-foreground">{alert.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-[9px] border-border/30 font-mono"><TrendingUp className="w-2.5 h-2.5 mr-0.5" /> {alert.conditions?.rules?.length > 0 ? conditionSummary(alert.conditions, metricLabel) : `${metricLabel(alert.metric)} ${alert.operator === 'gt' ? '>' : alert.operator === 'lt' ? '<' : alert.operator === 'gte' ? '>=' : '<='} ${alert.threshold}`}</Badge>
                    <Badge className={`text-[9px] ${SEV_COLORS[alert.severity]}`}>{alert.severity}</Badge>
                    <Badge variant="outline" className="text-[9px] border-border/30"><Clock className="w-2.5 h-2.5 mr-0.5" /> {formatSchedule(alert.schedule || { type: alert.check_frequency })}</Badge>
                    {alert.date_preset && <Badge variant="outline" className="text-[9px] border-indigo-500/30 text-indigo-300" data-testid={`alert-period-${alert.alert_id}`}>{{ today: 'Today', this_month_to_date: 'MTD', this_quarter_to_date: 'QTD', this_year_to_date: 'YTD', inception: 'ITD' }[alert.date_preset] || alert.date_preset}</Badge>}
                    {alert.predictive && <Badge variant="outline" className="text-[9px] border-indigo-500/40 text-indigo-300" data-testid={`alert-predictive-${alert.alert_id}`}><TrendingUp className="w-2.5 h-2.5 mr-0.5" /> Predictive · {alert.forecast_horizon || 6} {alert.forecast_unit || 'month'}(s)</Badge>}
                    {alert.predictive && alert.last_forecast?.confidence && (
                      <Badge className={`text-[9px] ${alert.last_forecast.confidence === 'High' ? 'bg-emerald-500/15 text-emerald-300' : alert.last_forecast.confidence === 'Medium' ? 'bg-amber-500/15 text-amber-300' : 'bg-rose-500/15 text-rose-300'}`} data-testid={`alert-confidence-${alert.alert_id}`} title={`Forecast confidence based on backtest error (MAPE ${alert.last_forecast.mape ?? '—'}%)`}>{alert.last_forecast.confidence} confidence</Badge>
                    )}
                    {alert.snoozed_until && new Date(alert.snoozed_until) > new Date() && <Badge variant="outline" className="text-[9px] border-amber-500/30 text-amber-400" data-testid={`alert-snoozed-${alert.alert_id}`}><BellOff className="w-2.5 h-2.5 mr-0.5" /> Snoozed until {new Date(alert.snoozed_until).toLocaleDateString()}</Badge>}
                  </div>
                  <div className="flex items-center gap-1 mt-1.5 text-[10px] text-muted-foreground"><Mail className="w-2.5 h-2.5" /> {alert.recipients?.join(', ')}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Tooltip><TooltipTrigger asChild><button onClick={() => handleEvaluate(alert.alert_id)} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground" data-testid={`alert-test-${alert.alert_id}`}><Play className="w-3.5 h-3.5" /></button></TooltipTrigger><TooltipContent><p className="text-xs">Test now (notifies on breach)</p></TooltipContent></Tooltip>
                  <Tooltip><TooltipTrigger asChild><button onClick={() => openHistory(alert)} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground" data-testid={`alert-history-${alert.alert_id}`}><History className="w-3.5 h-3.5" /></button></TooltipTrigger><TooltipContent><p className="text-xs">Evaluation history</p></TooltipContent></Tooltip>
                  {(() => {
                    const isSnoozed = alert.snoozed_until && new Date(alert.snoozed_until) > new Date();
                    return (
                      <Popover>
                        <Tooltip><TooltipTrigger asChild>
                          <PopoverTrigger asChild>
                            <button className={`p-1.5 rounded-lg hover:bg-muted/50 ${isSnoozed ? 'text-amber-400' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`alert-snooze-${alert.alert_id}`}><BellOff className="w-3.5 h-3.5" /></button>
                          </PopoverTrigger>
                        </TooltipTrigger><TooltipContent><p className="text-xs">{isSnoozed ? 'Snoozed — manage' : 'Snooze'}</p></TooltipContent></Tooltip>
                        <PopoverContent align="end" className="w-44 p-1.5" data-testid={`alert-snooze-menu-${alert.alert_id}`}>
                          <p className="text-[10px] text-muted-foreground px-1.5 py-1">Mute this alert for</p>
                          {[['1 hour', 60], ['4 hours', 240], ['1 day', 1440], ['1 week', 10080]].map(([lbl, mins]) => (
                            <button key={mins} onClick={() => handleSnooze(alert.alert_id, mins)} className="w-full text-left text-xs px-2 py-1.5 rounded-md hover:bg-muted/50 text-foreground" data-testid={`alert-snooze-${mins}`}>{lbl}</button>
                          ))}
                          {isSnoozed && <button onClick={() => handleSnooze(alert.alert_id, 0)} className="w-full text-left text-xs px-2 py-1.5 rounded-md hover:bg-muted/50 text-amber-400 border-t border-border/30 mt-1" data-testid="alert-unsnooze">Un-snooze now</button>}
                        </PopoverContent>
                      </Popover>
                    );
                  })()}
                  <Tooltip><TooltipTrigger asChild><button onClick={() => openEdit(alert)} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground"><Pencil className="w-3.5 h-3.5" /></button></TooltipTrigger><TooltipContent><p className="text-xs">Edit</p></TooltipContent></Tooltip>
                  <VersionHistoryButton resourceType="alert" resourceId={alert.alert_id} resourceName={alert.name} />
                  <Tooltip><TooltipTrigger asChild><button onClick={() => setConfirmDeleteAlert(alert)} className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive" data-testid={`delete-alert-${alert.alert_id}`}><Trash2 className="w-3.5 h-3.5" /></button></TooltipTrigger><TooltipContent><p className="text-xs">Delete</p></TooltipContent></Tooltip>
                </div>
                <Switch checked={alert.enabled} onCheckedChange={() => handleToggle(alert.alert_id)} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Alert Dialog */}
      <Dialog open={alertDialogOpen} onOpenChange={setAlertDialogOpen}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader><DialogTitle className="font-heading">{editAlert ? 'Edit Alert' : 'Create Alert'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2 min-w-0">
            {/* Draft with AI */}
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-2.5">
              <Label className="text-[11px] text-primary flex items-center gap-1.5 mb-1"><Sparkles className="w-3.5 h-3.5" /> Describe it and let AI build the alert</Label>
              <div className="flex gap-1.5">
                <Input
                  value={alertAiText}
                  onChange={e => setAlertAiText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); draftWithAi(); } }}
                  placeholder="e.g. warn me when loss ratio is projected to exceed 75% in the next 3 months"
                  className="rounded-lg bg-muted/50 border-border/50 text-foreground text-xs min-w-0"
                  data-testid="alert-ai-input"
                />
                <Button onClick={draftWithAi} disabled={aiDrafting || !alertAiText.trim()} className="rounded-lg bg-primary text-primary-foreground text-xs shrink-0 px-3" data-testid="alert-ai-draft-btn">
                  {aiDrafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                </Button>
              </div>
              <p className="text-[9px] text-muted-foreground mt-1">Pick a data source below first for the best metric match. You can edit anything after drafting.</p>
              {aiDraftPreview && (
                <div className="mt-2 rounded-lg border border-primary/30 bg-background/70 p-2" data-testid="alert-ai-preview">
                  <p className="text-[10px] text-muted-foreground mb-0.5">AI understood:</p>
                  <p className="text-xs text-foreground font-medium break-words" data-testid="alert-ai-preview-text">{draftSummary(aiDraftPreview)}</p>
                  <div className="flex gap-1.5 mt-2">
                    <Button onClick={applyDraft} className="rounded-lg bg-primary text-primary-foreground text-[10px] h-7 px-3" data-testid="alert-ai-apply">Use this</Button>
                    <Button onClick={() => setAiDraftPreview(null)} variant="outline" className="rounded-lg text-[10px] h-7 px-3" data-testid="alert-ai-discard">Discard</Button>
                  </div>
                </div>
              )}
            </div>
            <div><Label className="text-xs text-muted-foreground">Alert Name</Label><Input value={alertName} onChange={e => setAlertName(e.target.value)} placeholder="e.g. Loss Ratio Exceeds 75%" className="rounded-lg bg-muted/50 border-border/50 mt-1 text-foreground" /></div>
            <div><Label className="text-xs text-muted-foreground">Data Source</Label>
              <div className="flex gap-1 mt-1 mb-1.5">
                <button onClick={() => setAlertSourceType('model')} className={`flex-1 text-[9px] py-1.5 rounded-md ${alertSourceType === 'model' ? 'bg-primary/20 text-primary font-medium' : 'text-muted-foreground hover:bg-muted/20'}`}>Semantic Model</button>
                <button onClick={() => setAlertSourceType('direct')} className={`flex-1 text-[9px] py-1.5 rounded-md ${alertSourceType === 'direct' ? 'bg-primary/20 text-primary font-medium' : 'text-muted-foreground hover:bg-muted/20'}`}>Data Source</button>
              </div>
              {alertSourceType === 'model' ? (
                <Select value={alertModelId} onValueChange={v => { setAlertModelId(v); api.getSemanticModelFull(v).then(m => { const f = []; (m.entities || []).forEach(e => (e.attributes || []).forEach(a => { if (!f.some(ff => ff.name === a.name)) f.push(a); })); setAlertFields(f); }).catch(() => {}); }}>
                  <SelectTrigger className="rounded-lg bg-muted/50 border-border/50"><SelectValue placeholder="Select model" /></SelectTrigger>
                  <SelectContent>{smModels.map(m => <SelectItem key={m.model_id} value={m.model_id} className="text-xs">{m.name}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Select value={alertCollection} onValueChange={v => { setAlertCollection(v); api.introspectCollection(v).then(res => setAlertFields((res.columns || []).map(c => ({ name: c.name, semantic_type: c.data_type === 'number' ? 'measure' : 'dimension', data_type: c.data_type })))).catch(() => {}); }}>
                  <SelectTrigger className="rounded-lg bg-muted/50 border-border/50"><SelectValue placeholder="Select data source" /></SelectTrigger>
                  <SelectContent>{alertDataSources.filter(ds => ds.collection_name).map(ds => <SelectItem key={ds.source_id} value={ds.collection_name} className="text-xs">{ds.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div><Label className="text-xs text-muted-foreground flex items-center justify-between">
              <span>Conditions</span>
              <button onClick={() => { setUseAdvanced(!useAdvanced); if (!useAdvanced && alertConditions.rules.length === 0) setAlertConditions({ logic: 'AND', rules: [{ metric: alertMetric || '', operator: alertOp || 'gt', threshold: alertThreshold || '' }] }); }} className="text-[9px] text-primary hover:text-primary/80 flex items-center gap-1" data-testid="toggle-advanced">
                <GitBranch className="w-3 h-3" /> {useAdvanced ? 'Simple mode' : 'Advanced (AND/OR)'}
              </button>
            </Label>
            {alertDimensionFilters.length > 0 && (
              <div className="mt-1 mb-2 flex flex-wrap gap-1.5" data-testid="alert-dimension-scope">
                {alertDimensionFilters.map((df, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md bg-indigo-500/10 border border-indigo-500/30 text-indigo-300" data-testid={`alert-dim-filter-${i}`}>
                    {(alertFields.find(f => f.name === df.field) || {}).business_name || (alertFields.find(f => f.name === df.field) || {}).display_name || df.field} = {df.value}
                    <button onClick={() => setAlertDimensionFilters(prev => prev.filter((_, j) => j !== i))} className="hover:text-white" data-testid={`alert-dim-filter-remove-${i}`}>×</button>
                  </span>
                ))}
              </div>
            )}
            {useAdvanced ? (
              <div className="mt-2 p-3 rounded-lg border border-border/30 bg-muted/10">
                <ConditionBuilder conditions={alertConditions} onChange={setAlertConditions} fields={alertFields} />
              </div>
            ) : (
              <>
                <div className="mt-1">
                  {alertFields.length > 0 ? (
                    <Select value={alertMetric} onValueChange={setAlertMetric}>
                      <SelectTrigger className="rounded-lg bg-muted/50 border-border/50" data-coach-target="alert-metric-picker"><SelectValue placeholder="Select metric" /></SelectTrigger>
                      <SelectContent>{alertFields.filter(f => f.semantic_type === 'measure' || f.data_type === 'number' || f.data_type === 'float').map(f => <SelectItem key={f.name} value={f.name} className="text-xs">{f.business_name || f.display_name || f.name}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <Input value={alertMetric} onChange={e => setAlertMetric(e.target.value)} placeholder="Select a data source first" className="rounded-lg bg-muted/50 border-border/50 font-mono text-foreground" />}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div><Label className="text-xs text-muted-foreground">Operator</Label><Select value={alertOp} onValueChange={setAlertOp}><SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="gt">Greater than</SelectItem><SelectItem value="lt">Less than</SelectItem><SelectItem value="gte">Greater or equal</SelectItem><SelectItem value="lte">Less or equal</SelectItem><SelectItem value="eq">Equal to</SelectItem><SelectItem value="neq">Not equal to</SelectItem></SelectContent></Select></div>
                  <div><Label className="text-xs text-muted-foreground">Threshold</Label><Input type="number" value={alertThreshold} onChange={e => setAlertThreshold(e.target.value)} placeholder="75" className="rounded-lg bg-muted/50 border-border/50 mt-1 text-foreground" /></div>
                </div>
              </>
            )}</div>
            <div className="grid grid-cols-2 gap-2 [&>div]:min-w-0">
              <div><Label className="text-xs text-muted-foreground">Severity</Label><Select value={alertSeverity} onValueChange={setAlertSeverity}><SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="critical">Critical</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent></Select></div>
              <div><Label className="text-xs text-muted-foreground">Metric period</Label>
                <Select value={alertDatePreset || 'all_time'} onValueChange={v => setAlertDatePreset(v === 'all_time' ? '' : v)}>
                  <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-1" data-testid="alert-date-preset"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_time">All time</SelectItem>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="this_month_to_date">This month to date</SelectItem>
                    <SelectItem value="this_quarter_to_date">This quarter to date</SelectItem>
                    <SelectItem value="this_year_to_date">This year to date</SelectItem>
                    <SelectItem value="inception">Inception to date</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Predictive (forecast-based) alert */}
            <div className="rounded-lg border border-border/50 bg-muted/30 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-3.5 h-3.5 text-indigo-400" />
                  <Label className="text-xs text-foreground">Predictive alert</Label>
                </div>
                <Switch checked={alertPredictive} onCheckedChange={setAlertPredictive} data-testid="alert-predictive-toggle" />
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                {alertPredictive
                  ? 'Forecasts this metric and fires if a PROJECTED period breaches the threshold. The notification includes the forecast confidence level.'
                  : 'Fires on the current value. Turn on to fire on a projected future breach instead.'}
              </p>
              {alertPredictive && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Forecast horizon</Label>
                    <Input type="number" min={1} max={36} value={alertForecastHorizon} onChange={e => setAlertForecastHorizon(e.target.value)} className="rounded-lg bg-muted/50 border-border/50 mt-1 text-foreground" data-testid="alert-forecast-horizon" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Grain</Label>
                    <Select value={alertForecastUnit} onValueChange={setAlertForecastUnit}>
                      <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-1" data-testid="alert-forecast-unit"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="month">Monthly</SelectItem>
                        <SelectItem value="quarter">Quarterly</SelectItem>
                        <SelectItem value="year">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
            {/* Shared scheduling picker (unified across Exports / Alerts / Insights) */}
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Check schedule</Label>
              <SchedulePicker value={alertSchedule} onChange={setAlertSchedule} />
            </div>
            <div><Label className="text-xs text-muted-foreground">Recipients</Label><Input value={alertRecipients} onChange={e => setAlertRecipients(e.target.value)} placeholder="cfo@company.com" className="rounded-lg bg-muted/50 border-border/50 mt-1 text-foreground" /></div>
            <div>
              <Label className="text-xs text-muted-foreground">Notify on breach</Label>
              <div className="flex gap-1.5 mt-1" data-testid="alert-channels">
                {[['email', 'Email', Mail], ['slack', 'Slack', Slack], ['teams', 'Teams', Users]].map(([val, lbl, Icon]) => {
                  const on = alertChannels.includes(val);
                  return (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setAlertChannels(prev => on ? prev.filter(c => c !== val) : [...prev, val])}
                      className={`flex-1 inline-flex items-center justify-center gap-1.5 text-[10px] py-1.5 rounded-md border transition-colors ${on ? 'bg-primary/20 border-primary/40 text-primary font-medium' : 'border-border/40 text-muted-foreground hover:bg-muted/20'}`}
                      data-testid={`alert-channel-${val}`}
                    ><Icon className="w-3 h-3" /> {lbl}</button>
                  );
                })}
              </div>
              {alertChannels.includes('slack') && (
                <div className="mt-1.5">
                  <SlackChannelPicker value={alertSlackChannel} onChange={setAlertSlackChannel} multiple={false} />
                </div>
              )}
            </div>
            <Button onClick={handleSave} disabled={saving || !alertName.trim() || !alertMetric.trim()} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95" data-coach-target="alert-save-button">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              {editAlert ? 'Save Changes' : 'Create Alert'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDeleteAlert} onOpenChange={o => { if (!o) setConfirmDeleteAlert(null); }}>
        <AlertDialogContent className="glass-card border-border/50" data-testid="alert-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete this alert?</AlertDialogTitle>
            <AlertDialogDescription>
              “{confirmDeleteAlert?.name}” will be permanently removed. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="alert-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { const id = confirmDeleteAlert?.alert_id; setConfirmDeleteAlert(null); if (id) handleDelete(id); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="alert-delete-confirm-btn"
            >Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Alert History drawer */}
      <Dialog open={!!historyAlert} onOpenChange={o => { if (!o) setHistoryAlert(null); }}>
        <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-lg max-h-[80vh] overflow-auto" data-testid="alert-history-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><History className="w-4 h-4" /> {historyAlert?.name} — Evaluation History</DialogTitle></DialogHeader>
          <div className="mt-2">
            {historyAlert && (
              <Button onClick={() => handleEvaluate(historyAlert.alert_id)} variant="outline" className="rounded-lg text-xs mb-3" data-testid="alert-history-run"><Play className="w-3.5 h-3.5 mr-1.5" /> Run evaluation now</Button>
            )}
            {historyLoading ? (
              <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
            ) : historyRows.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No evaluations yet. Run one to start the history.</p>
            ) : (
              <div className="space-y-2" data-testid="alert-history-list">
                {historyRows.map(ev => (
                  <div key={ev.eval_id} className="flex items-center justify-between rounded-lg border border-border/30 bg-muted/10 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${ev.breached ? 'bg-red-500' : 'bg-emerald-500'}`} />
                      <div>
                        <p className="text-xs font-mono text-foreground">{typeof ev.current_value === 'number' ? ev.current_value.toLocaleString() : (ev.current_value ?? '—')} <span className="text-muted-foreground/80">vs {ev.operator} {ev.threshold}</span></p>
                        <p className="text-[10px] text-muted-foreground">{new Date(ev.evaluated_at).toLocaleString()}{ev.date_preset ? ` · ${ev.date_preset}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {ev.notified && <Badge variant="outline" className="text-[8px] border-sky-500/30 text-sky-300"><Bell className="w-2 h-2 mr-0.5" /> sent</Badge>}
                      <Badge className={`text-[9px] ${ev.breached ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'}`}>{ev.breached ? 'Breached' : 'OK'}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
}
